import { FetchApi } from '@backstage/core-plugin-api';
import { delay } from './helpers';
import { AuthService, LoggerService } from '@backstage/backend-plugin-api';

/**
 * Fetch API that adds the current token to the headers
 */
export class AuthenticatedFetchApi implements FetchApi {
  private readonly _authService: AuthService;
  private readonly _pluginId: string;
  private readonly _maxRetries = 3;
  private readonly _retryDelay = 1000;
  private _logger: LoggerService;

  constructor(
    authService: AuthService,
    logger: LoggerService,
    pluginId: string,
  ) {
    this._authService = authService;
    this._logger = logger;
    this._pluginId = pluginId;
  }

  /**
   * Fetches the given input with the given init, adding the current token to the headers
   * @param input  The input for the fetch
   * @param init The init for the fetch
   */
  public async fetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const { token } = await this._authService.getPluginRequestToken({
      onBehalfOf: await this._authService.getOwnServiceCredentials(),
      targetPluginId: this._pluginId, // e.g. 'catalog'
    });
    const updatedInit = {
      ...init,
      headers: {
        ...init?.headers,
        Authorization: `Bearer ${token}`,
      },
    };

    let retries = 0;
    while (retries < this._maxRetries) {
      try {
        return await fetch(input, updatedInit);
      } catch (error: any) {
        if (error?.cause?.code === 'ECONNRESET') {
          retries++;
          this._logger.warn(
            `Connection reset by peer. Retrying (${retries}/${this._maxRetries})...`,
          );
          await delay(this._retryDelay);
        } else {
          throw error;
        }
      }
    }

    throw new Error('Max retries exceeded. Connection reset by peer.');
  }
}
